/**
 * Prebid video (PRD §12.3). A toggle, never a dependency: any failure drops to the GAM-only path.
 * Assumption A7 (Q7): the player only uses a pbjs instance the page already has; it never loads Prebid.
 */

/**
 * Stable 0-99 bucket from a string (A/B cookie value).
 * @param {string} value
 * @returns {number}
 */
export function bucketOf(value) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  return hash % 100;
}

/**
 * Decide whether this page view runs Prebid (§12.3 toggle, abPercent, ?qsvp_prebid override).
 * @param {object} config final config
 * @param {string|null} abCookieValue value of the A/B cookie
 * @returns {boolean}
 */
export function shouldUsePrebid(config, abCookieValue) {
  if (config.prebidForce === true || config.prebidForce === false) return config.prebidForce;
  if (config.prebid.enabled) return true;
  if (config.prebid.abPercent > 0 && abCookieValue) return bucketOf(abCookieValue + ':prebid') < config.prebid.abPercent;
  return false;
}

/**
 * Prebid video ad unit matching the GAM request exactly (§12.3).
 * @param {object} input
 * @param {string} input.code adUnitCode
 * @param {[number, number]} input.playerSize real player size
 * @param {'auto'|'click'} input.startType
 * @param {number} input.maxDurationSeconds 30 for pre-roll 1, else the remaining ad budget
 * @param {object[]} input.bidders bidder configs
 * @returns {object}
 */
export function buildPrebidVideoUnit({ code, playerSize, startType, maxDurationSeconds, bidders }) {
  return {
    code,
    mediaTypes: {
      video: {
        context: 'instream',
        plcmt: 2, // C1: same declaration as the GAM tag
        playerSize: [playerSize],
        mimes: ['video/mp4'], // 2.0.6: the player plays MP4 only
        protocols: [2, 3, 5, 6, 7, 8],
        api: [7], // OMID only, no VPAID
        playbackmethod: [startType === 'click' ? 3 : 6],
        startdelay: 0,
        linearity: 1,
        minduration: 5,
        maxduration: maxDurationSeconds,
        skip: 1,
      },
    },
    bids: bidders || [],
  };
}

/**
 * Run one Prebid auction and build the GAM URL with Prebid's key-values (§12.3 steps 2-3).
 * Resolves null on any problem (fails safe to GAM-only).
 * @param {object} input
 * @param {object|undefined} input.pbjs window.pbjs
 * @param {object} input.unit Prebid ad unit
 * @param {number} input.timeoutMs 1000 cap
 * @param {object} input.gamParams { iu, description_url }
 * @param {Function} input.setTimeoutFn
 * @returns {Promise<string|null>}
 */
export function runPrebidAuction({ pbjs, unit, timeoutMs, gamParams, setTimeoutFn }) {
  return new Promise((resolve) => {
    if (!pbjs || !pbjs.que || typeof pbjs.que.push !== 'function') return resolve(null);
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    setTimeoutFn(() => finish(null), timeoutMs + 200); // hard cap: never block the player
    pbjs.que.push(() => {
      try {
        if (typeof pbjs.removeAdUnit === 'function') pbjs.removeAdUnit(unit.code);
        pbjs.addAdUnits([unit]); // Shared Prebid: add our unit to the page's instance
        pbjs.requestBids({
          adUnitCodes: [unit.code],
          timeout: timeoutMs,
          bidsBackHandler: () => {
            try {
              const url = pbjs.adServers.gam.buildVideoUrl({
                adUnit: unit,
                params: { iu: gamParams.iu, output: 'vast', description_url: gamParams.description_url },
              });
              finish(url || null);
            } catch (error) {
              finish(null);
            }
          },
        });
      } catch (error) {
        finish(null);
      }
    });
  });
}
